import { useEffect, useMemo, useState } from 'react';
import {
  type Query,
  type QueryFunctionContext,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query';

import {
  getGetScenarioQueryKey,
  getGetScenariosQueryKey,
  useGetScenarioHook,
} from '@/api/generated';
import {
  type GetScenariosParams,
  type ScenarioListResponse,
  type ScenarioResponse,
} from '@/api/generated.schemas';
import { type Scenarios } from '@/routes/(app)/(scenarios)/types';
import {
  getIncompleteScenarioIds,
  getScenarioProgressPollInterval,
  isNotFoundError,
  patchScenarioInListResponse,
  removeScenarioFromListResponse,
  SCENARIOS_PROGRESS_POLL_MAX_RETRIES,
  shouldInvalidateScenariosListForPoll,
} from '@/routes/(app)/(scenarios)/utils/scenarios-list.utils';

export const useScenariosProgressPolling = (
  scenarios: Scenarios,
  params: GetScenariosParams,
  isPlaceholderData: boolean
) => {
  const queryClient = useQueryClient();
  const getScenario = useGetScenarioHook();
  const scenariosListPrefixKey = getGetScenariosQueryKey();
  const [missingScenarioIds, setMissingScenarioIds] = useState(
    () => new Set<string>()
  );

  const incompleteIds = useMemo(() => {
    if (isPlaceholderData) {
      return [];
    }

    return getIncompleteScenarioIds(scenarios).filter(
      (scenarioId) => !missingScenarioIds.has(scenarioId)
    );
  }, [isPlaceholderData, missingScenarioIds, scenarios]);

  const handleScenarioGone = (scenarioId: string) => {
    setMissingScenarioIds((current) => {
      if (current.has(scenarioId)) {
        return current;
      }

      const next = new Set(current);
      next.add(scenarioId);
      return next;
    });
    queryClient.setQueriesData<ScenarioListResponse>(
      { queryKey: scenariosListPrefixKey },
      (current) => removeScenarioFromListResponse(current, scenarioId)
    );
  };

  useEffect(() => {
    for (const scenarioId of missingScenarioIds) {
      queryClient.removeQueries({
        queryKey: getGetScenarioQueryKey(scenarioId),
      });
    }
  }, [missingScenarioIds, queryClient]);

  useQueries({
    queries: incompleteIds.map((scenarioId) => ({
      queryKey: getGetScenarioQueryKey(scenarioId),
      queryFn: async ({ signal }: QueryFunctionContext) => {
        try {
          const scenario = await getScenario(scenarioId, signal);
          const listQueryKey = getGetScenariosQueryKey(params);
          const currentList =
            queryClient.getQueryData<ScenarioListResponse>(listQueryKey);
          const shouldInvalidate = shouldInvalidateScenariosListForPoll(
            currentList,
            scenario,
            params.order_by
          );

          queryClient.setQueryData<ScenarioListResponse>(
            listQueryKey,
            (cached) => patchScenarioInListResponse(cached, scenario)
          );

          if (shouldInvalidate) {
            await queryClient.invalidateQueries({
              queryKey: scenariosListPrefixKey,
            });
          }

          return scenario;
        } catch (error) {
          if (isNotFoundError(error)) {
            handleScenarioGone(scenarioId);
            await queryClient.invalidateQueries({
              queryKey: scenariosListPrefixKey,
            });
          }
          throw error;
        }
      },
      refetchInterval: (query: Query<ScenarioResponse, Error>) =>
        getScenarioProgressPollInterval(query.state.data?.progress),
      retry: (failureCount: number, error: unknown) => {
        if (isNotFoundError(error)) {
          return false;
        }
        return failureCount < SCENARIOS_PROGRESS_POLL_MAX_RETRIES;
      },
    })),
  });
};
